// @ts-nocheck
/**
 * Emails a Live Sheet share link that already exists. Sending never creates a
 * new token — editing recipients only updates allowed_emails on the token, so
 * the URL already in people's inboxes keeps working.
 */
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import RecipientChipsInput from "@/components/events/RecipientChipsInput";

export default function SendShareLinkForm({
  tokenId,
  recipients = [],
  syncAllowedEmails = true,
  onSent,
  onSkip,
  skipLabel = "Skip",
}: {
  tokenId: string;
  recipients?: string[];
  syncAllowedEmails?: boolean;
  onSent?: () => void;
  onSkip?: () => void;
  skipLabel?: string;
}) {
  const [list, setList] = useState<string[]>(() =>
    Array.from(new Set((recipients ?? []).filter(Boolean).map((e) => e.toLowerCase()))),
  );
  const [sentMap, setSentMap] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  // Real send state, read from the access log rather than the browser.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await (supabase as any)
        .from("event_sheet_token_access")
        .select("viewer_email, created_at, outcome")
        .eq("token_id", tokenId)
        .eq("action", "sent")
        .order("created_at", { ascending: false });
      if (cancelled) return;
      const map: Record<string, string> = {};
      for (const row of data ?? []) {
        const email = String(row.viewer_email ?? "").toLowerCase();
        if (!email || row.outcome === "failed") continue;
        if (!map[email]) map[email] = row.created_at;
      }
      setSentMap(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [tokenId]);

  const newOnes = useMemo(() => list.filter((e) => !sentMap[e]), [list, sentMap]);
  const alreadySent = useMemo(() => list.filter((e) => sentMap[e]), [list, sentMap]);

  // Adding or removing a chip updates allowed_emails on the existing token.
  const applyChange = async (next: string[]) => {
    const prev = list;
    setList(next);
    if (!syncAllowedEmails) return;
    const { error } = await (supabase as any)
      .from("event_sheet_tokens")
      .update({ allowed_emails: next.length ? next : null })
      .eq("id", tokenId);
    if (error) {
      console.error("[SendShareLinkForm] allowed_emails update failed", error);
      toast.error("Couldn't update who can open this link.");
      setList(prev);
    }
  };

  const send = async (targets: string[]) => {
    if (targets.length === 0) {
      toast.error("Add at least one email address.");
      return;
    }
    setBusy(true);
    try {
      const { data, error } = await invokeEdgeFunction("send-sheet-share", {
        body: { token_id: tokenId, recipients: targets, origin: window.location.origin },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error?.message || "Nothing was sent.");
      toast.success(
        data.failed
          ? `Sent to ${data.sent}, ${data.failed} failed.`
          : `Sent to ${data.sent} ${data.sent === 1 ? "person" : "people"}.`,
      );
      const now = new Date().toISOString();
      setSentMap((m) => {
        const next = { ...m };
        for (const t of targets) next[t] = next[t] ?? now;
        return next;
      });
      onSent?.();
    } catch (e: any) {
      console.error("[SendShareLinkForm] send failed", e);
      toast.error(e.message || "Couldn't send the email.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label>
        {newOnes.length
          ? `Send to ${newOnes.length} new ${newOnes.length === 1 ? "person" : "people"}?`
          : "Recipients"}
      </Label>
      <RecipientChipsInput value={list} onChange={applyChange} sentMap={sentMap} />
      {newOnes.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Only {newOnes.join(", ")} will be emailed.
        </p>
      )}
      {newOnes.length === 0 && alreadySent.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Everyone here already has the link.
        </p>
      )}
      <div className="flex gap-2">
        <Button
          className="flex-1"
          onClick={() => send(newOnes)}
          disabled={busy || newOnes.length === 0}
        >
          {busy ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Mail className="mr-2 h-4 w-4" />
          )}
          {newOnes.length
            ? `Send to ${newOnes.length} new ${newOnes.length === 1 ? "person" : "people"}`
            : "Send"}
        </Button>
        {onSkip && (
          <Button variant="outline" onClick={onSkip} disabled={busy}>
            {skipLabel}
          </Button>
        )}
      </div>
      {alreadySent.length > 0 && (
        <button
          type="button"
          disabled={busy}
          className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-50"
          onClick={() => send(list)}
        >
          Re-send to all {list.length}
        </button>
      )}
    </div>
  );
}
